import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, ArrowRight, ChevronLeft, ChevronRight, Sparkles, Gift, Tag, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, ShieldCheck, ClipboardPaste, X } from 'lucide-react';
import api from '../../services/api';
import { REELISLE_LOGO_WIDE } from '../../lib/brandAssets';
import { isAdminPortalHost, isAgentPortalHost, getPlayerOrigin } from '../../lib/adminPortal';
import { bannerSlideDurationMs, optimizedUploadUrl } from '../../lib/utils';

function normalizeSupportLink(value, kind) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (kind === 'telegram') return `https://t.me/${raw.replace(/^@/, '').replace(/^t\.me\//i, '')}`;
  if (kind === 'whatsapp') {
    if (/^(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com|whatsapp\.com)\//i.test(raw)) return `https://${raw}`;
    return `https://wa.me/${raw.replace(/[^\d+]/g, '').replace(/^\+/, '')}`;
  }
  if (kind === 'messenger') return `https://m.me/${raw.replace(/^m\.me\//i, '').replace(/^@/, '')}`;
  return `https://${raw}`;
}

export default function AuthPage() {
  const { user, login, register, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminPortal = isAdminPortalHost();
  const agentPortal = isAgentPortalHost();
  const staffOnlyPortal = adminPortal;
  const requestedMode = searchParams.get('mode');
  const requestedAuthMode = ['register', 'signup', 'sign-up'].includes(requestedMode || '')
    ? 'register'
    : ['login', 'signin', 'sign-in'].includes(requestedMode || '')
      ? 'login'
      : '';
  const initialMode = !staffOnlyPortal && requestedAuthMode === 'register' ? 'register' : 'login';
  
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [banners, setBanners] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [bannerTransition, setBannerTransition] = useState(true);
  const [supportChannels, setSupportChannels] = useState({});
  const [supportOpen, setSupportOpen] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [referralCode, setReferralCode] = useState(agentPortal ? '' : (searchParams.get('ref') || ''));
  const [referralOpen, setReferralOpen] = useState(!agentPortal && Boolean(searchParams.get('ref')));
  const [referralStatus, setReferralStatus] = useState({ state: (!agentPortal && searchParams.get('ref')) ? 'idle' : 'empty', message: '' });
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [signupOtp, setSignupOtp] = useState('');
  const [signupOtpSent, setSignupOtpSent] = useState(false);
  const [signupStep, setSignupStep] = useState('form');
  const [sendingSignupOtp, setSendingSignupOtp] = useState(false);
  // Track the referral channel (?src=qr|share|copy|...) so admin analytics
  // can break down which invite method drives the most signups.
  const [referralSource] = useState(searchParams.get('src') || '');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (staffOnlyPortal) return;
    const bannerType = agentPortal ? 'agent_login_banner' : 'login_banner';
    api.get(`/api/announcements?banner_type=${bannerType}`)
      .then(res => setBanners((res.data.announcements || []).filter((item) => item?.image_url)))
      .catch(() => {});
  }, [agentPortal, staffOnlyPortal]);

  useEffect(() => {
    if (staffOnlyPortal) return;
    let alive = true;
    api.get('/api/public/settings/support-channels')
      .then((res) => {
        if (alive) setSupportChannels(res.data?.support_channels || {});
      })
      .catch(() => {
        if (alive) setSupportChannels({});
      });
    return () => { alive = false; };
  }, [staffOnlyPortal]);

  useEffect(() => {
    if (staffOnlyPortal && mode !== 'login') {
      setMode('login');
    }
  }, [staffOnlyPortal, mode]);

  useEffect(() => {
    if (staffOnlyPortal) return;
    const nextMode = requestedAuthMode;
    if (!nextMode) return;
    setMode(nextMode);
    setEmailError('');
    if (nextMode !== 'register') {
      setSignupStep('form');
      setSignupOtp('');
      setSignupOtpSent(false);
    }
  }, [staffOnlyPortal, requestedAuthMode]);
  const displayBanners = agentPortal && banners.length ? banners : [];
  const authCarouselBanners = displayBanners.length > 1 ? [...displayBanners, { ...displayBanners[0], __clone: true }] : displayBanners;
  const visibleBannerIdx = displayBanners.length ? bannerIdx % displayBanners.length : 0;
  const currentBannerDurationMs = bannerSlideDurationMs(displayBanners[visibleBannerIdx], 7000);

  // Fetch the active signup bonus for this auth page's scope so we can
  // advertise "Get X FP free" when the player switches to Sign Up mode.
  const [signupBonus, setSignupBonus] = useState(0);
  // Iter 142.8: surface tenant slug at component scope so the Google
  // OAuth start handler can pass it through to the backend.
  const tenantSlug = (() => {
    const slug = window.location.pathname.startsWith('/') ? window.location.pathname.split('/').filter(Boolean)[0] : '';
    const reserved = new Set(['auth', 'admin', 'app', 'referral-staff', 'staff-portal', 'finance']);
    return slug && !reserved.has(slug) ? slug : '';
  })();
  useEffect(() => {
    const q = tenantSlug ? `?tenant_slug=${encodeURIComponent(tenantSlug)}` : '';
    api.get(`/api/auth/signup-bonus${q}`)
      .then(res => { if (res.data?.enabled) setSignupBonus(res.data.amount_fp ?? res.data.amount_sc ?? 0); })
      .catch(() => {});
  }, [tenantSlug]);

  // Auto-rotate banners
  useEffect(() => {
    setBannerTransition(false);
    setBannerIdx(0);
    window.requestAnimationFrame(() => setBannerTransition(true));
  }, [displayBanners.length]);

  useEffect(() => {
    if (displayBanners.length <= 1) return undefined;
    const timer = setTimeout(() => {
      setBannerTransition(true);
      setBannerIdx(i => i + 1);
    }, currentBannerDurationMs);
    return () => clearTimeout(timer);
  }, [bannerIdx, currentBannerDurationMs, displayBanners.length]);

  const handleBannerTransitionEnd = () => {
    if (displayBanners.length > 1 && bannerIdx >= displayBanners.length) {
      setBannerTransition(false);
      setBannerIdx(0);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setBannerTransition(true));
      });
    }
  };

  const bannerUrl = (banner) => {
    const url = banner?.image_url || '';
    const optimized = optimizedUploadUrl(url, 900, 72);
    if (/^(https?:|data:|blob:)/i.test(optimized)) return optimized;
    if (optimized.startsWith('/')) return optimized;
    return `/${optimized.replace(/^\/+/, '')}`;
  };

  useEffect(() => {
    if (user) {
      if (adminPortal) {
        if (['admin', 'moderator', 'support'].includes(user.role)) {
          navigate('/admin', { replace: true });
        } else {
          logout();
          toast.error('Player accounts must use reelisle.com');
        }
        return;
      }
      if (agentPortal) {
        if (user.role === 'referral_agent') {
          navigate('/referral-staff', { replace: true });
        } else {
          logout();
          window.location.href = `${getPlayerOrigin()}/auth`;
        }
        return;
      }
      // Honour ?next=<path> when the player landed here from a payment
      // return (DepositReturnPage) — send them straight back to their
      // wallet so the deposit flow completes seamlessly.
      const next = searchParams.get('next');
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        navigate(next, { replace: true });
        return;
      }
      if (['admin', 'moderator', 'support'].includes(user.role)) {
        navigate('/admin');
      } else if (user.role === 'referral_agent') {
        navigate('/referral-staff');
      } else if (user.role === 'staff') {
        navigate('/staff-portal');
      } else {
        navigate('/app');
      }
    }
  }, [user, navigate, searchParams, adminPortal, agentPortal, logout]);

  const resetSignupEmailVerification = () => {
    setSignupOtp('');
    setSignupOtpSent(false);
    setSignupStep('form');
  };

  const switchAuthMode = (nextMode) => {
    setMode(nextMode);
    setEmailError('');
    if (nextMode !== 'register') resetSignupEmailVerification();
  };

  const validateReferralCode = async ({ silent = false } = {}) => {
    const code = referralCode.trim();
    if (!code) {
      setReferralStatus({ state: 'empty', message: '' });
      return true;
    }

    setValidatingReferral(true);
    setReferralStatus({ state: 'checking', message: 'Checking referral code...' });
    try {
      const res = await api.get('/api/referrals/validate-code', { params: { code } });
      if (res.data?.valid) {
        const appliedCode = res.data.referral_code || code;
        setReferralCode(appliedCode);
        setReferralStatus({
          state: 'valid',
          message: 'Referral code applied successfully',
        });
        if (!silent) toast.success('Referral code applied successfully');
        return true;
      }
      setReferralStatus({ state: 'invalid', message: res.data?.message || 'Referral code not found' });
      if (!silent) toast.error(res.data?.message || 'Referral code not found');
      return false;
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.message || 'Could not validate referral code';
      setReferralStatus({ state: 'invalid', message });
      if (!silent) toast.error(message);
      return false;
    } finally {
      setValidatingReferral(false);
    }
  };

  const sendSignupOtp = async (signupEmail) => {
    const targetEmail = (signupEmail || email).trim();
    if (!targetEmail) {
      toast.error('Please enter your email');
      return false;
    }
    setSendingSignupOtp(true);
    try {
      const res = await api.post('/api/auth/signup/email/send-otp', { email: targetEmail });
      setSignupOtpSent(true);
      setSignupOtp('');
      setSignupStep('otp');
      toast.success(res.data?.message || 'Verification code sent to your email');
      return true;
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const backendMessage = err.response?.data?.message;
      const message = typeof detail === 'string' ? detail :
                      typeof backendMessage === 'string' ? backendMessage :
                      Array.isArray(detail) ? detail.map(item => item.msg).join(', ') :
                      'Could not send verification code';
      if (status === 409) setEmailError(message);
      toast.error(message);
      return false;
    } finally {
      setSendingSignupOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    const identifier = email.trim();
    const signupEmail = email.trim();
    if (mode === 'forgot') {
      if (!identifier) {
        toast.error('Please enter your username or email');
        return;
      }

      if (resetSent && (!resetCode.trim() || !resetPassword)) {
        toast.error('Please enter the reset code and your new password');
        return;
      }

      setLoading(true);
      try {
        if (!resetSent) {
          const res = await api.post('/api/auth/forgot-password/start', { identifier });
          setResetSent(true);
          toast.success(res.data?.message || 'If that account exists, a reset code has been sent.');
        } else {
          const res = await api.post('/api/auth/forgot-password/verify', {
            identifier,
            code: resetCode.trim(),
            new_password: resetPassword,
          });
          toast.success(res.data?.message || 'Password updated. Please sign in.');
          setMode('login');
          setResetSent(false);
          setResetCode('');
          setResetPassword('');
          setPassword('');
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        const backendMessage = err.response?.data?.message;
        const message = typeof detail === 'string' ? detail :
                        typeof backendMessage === 'string' ? backendMessage :
                        Array.isArray(detail) ? detail.map(item => item.msg).join(', ') :
                        'Password reset failed';
        toast.error(message);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (mode === 'register' ? (!signupEmail || !password) : (!identifier || !password)) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (mode === 'register' && !agentPortal && referralCode.trim()) {
      const referralOk = await validateReferralCode({ silent: true });
      if (!referralOk) {
        setReferralOpen(true);
        toast.error('Please fix or remove the referral code before signing up');
        return;
      }
    }

    if (mode === 'register' && (!signupOtpSent || signupStep !== 'otp')) {
      await sendSignupOtp(signupEmail);
      return;
    }

    if (mode === 'register' && !signupOtp.trim()) {
      toast.error('Enter the email verification code');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(identifier, password);
        toast.success('Welcome back!');
      } else {
        if (adminPortal) {
          toast.error('This portal supports staff sign-in only');
          return;
        }
        await register(signupEmail, password, null, null, agentPortal ? null : referralCode.trim(), null, referralSource, signupOtp.trim(), {
          accountType: agentPortal ? 'referral_agent' : undefined,
        });
        toast.success(agentPortal ? 'Partner account created!' : 'Email verified. Account created!');
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Backend returns 'message' field in contract-compliant error envelope
      const backendMessage = err.response?.data?.message;
      const message = typeof detail === 'string' ? detail :
                      typeof backendMessage === 'string' ? backendMessage :
                      Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                      'Authentication failed';
      if (mode === 'login' && message === 'Account disabled') {
        return;
      }
      // 409 Conflict on signup → inline error under the email field
      if (mode === 'register' && status === 409 && /email|username|taken|exists|in use/i.test(message)) {
        setEmailError(message);
        toast.error(message);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Kept dormant until Google sign-in is re-enabled in the UI.
  // eslint-disable-next-line no-unused-vars
  const handleGoogleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Iter 142.8: direct Google OAuth flow.
    // The redirect_uri is computed from window.location.origin so the
    // SAME code works on preview + prod + localhost.
    const redirectUri = window.location.origin + '/auth/google';
    try {
      const res = await api.post('/api/auth/google/start', {
        redirect_uri: redirectUri,
        referral_code: referralCode || undefined,
        tenant_slug: tenantSlug || undefined,
      });
      const url = res.data?.auth_url;
      if (!url) throw new Error('OAuth not configured');
      window.location.href = url;
    } catch (err) {
      const data = err.response?.data || {};
      toast.error(data.message || data.detail || err.message || 'Google sign-in is not configured');
    }
  };

  const pasteSignupOtp = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const code = String(text || '').replace(/\D/g, '').slice(0, 6);
      if (!code) {
        toast.error('No OTP code found in clipboard');
        return;
      }
      setSignupOtp(code);
    } catch {
      toast.error('Could not read clipboard');
    }
  };

  const isSignupOtpStep = !staffOnlyPortal && mode === 'register' && signupStep === 'otp';
  const supportLinks = [
    {
      key: 'messenger',
      label: 'Messenger',
      href: normalizeSupportLink(supportChannels.messenger, 'messenger'),
      color: '#1877F2',
      path: 'M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.81c.02.63.66 1.03 1.24.78l2.02-.89c.17-.08.36-.1.55-.06.88.24 1.82.37 2.72.37 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm5.89 7.41l-2.89 4.58c-.46.73-1.44.92-2.13.42l-2.3-1.73c-.17-.13-.41-.13-.58 0L7.5 14.67c-.31.24-.72-.13-.52-.47l2.89-4.58c.46-.73 1.44-.92 2.13-.42l2.3 1.73c.17.13.41.13.58 0l2.49-1.99c.31-.24.72.13.52.47z',
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      href: normalizeSupportLink(supportChannels.whatsapp, 'whatsapp'),
      color: '#25D366',
      path: 'M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm5.82 14.01c-.24.68-1.42 1.3-1.96 1.38-.5.07-1.13.1-1.82-.11-.42-.13-.95-.31-1.64-.61-2.89-1.24-4.77-4.17-4.91-4.36-.14-.19-1.14-1.52-1.14-2.9s.72-2.06.98-2.34c.26-.28.57-.35.76-.35.19 0 .38 0 .55.01.18.01.41-.07.65.49.24.57.82 2 .89 2.15.07.14.12.31.02.5-.1.19-.14.31-.28.47-.14.17-.3.37-.43.5-.14.14-.28.29-.12.57.17.28.74 1.22 1.58 1.97 1.09.97 2.01 1.27 2.29 1.41.28.14.45.12.61-.07.17-.19.71-.83.9-1.12.19-.28.38-.24.64-.14.26.1 1.64.77 1.92.91.28.14.47.21.54.33.07.12.07.68-.17 1.36z',
    },
    {
      key: 'telegram',
      label: 'Telegram',
      href: normalizeSupportLink(supportChannels.telegram, 'telegram'),
      color: '#229ED9',
      path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.03-1.99 1.27-5.62 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.06-.49-.83-.27-1.49-.42-1.43-.88.03-.24.37-.49 1.02-.75 3.99-1.73 6.65-2.87 7.97-3.44 3.8-1.58 4.59-1.86 5.1-1.87.11 0 .37.03.54.17.14.12.18.28.2.45-.01.06.01.24 0 .38z',
    },
  ].filter((channel) => channel.href);

  return (
    <div className="min-h-screen w-full max-w-[100vw] overflow-x-hidden text-white flex flex-col items-center justify-start px-4 sm:px-5 py-6" style={{ background: '#0B0E14' }} data-testid="auth-page">
      {/* Left Panel - Hero */}
      <div className="hidden relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, #20103d 0%, #140d26 40%, #0B0E14 100%)' }} />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(212,175,55,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(212,175,55,0.05)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 25% 30%, rgba(212,175,55,0.18), transparent 40%), radial-gradient(circle at 70% 70%, rgba(108,92,231,0.12), transparent 45%)',
          }}
        />
        <div className="relative z-10 flex flex-col justify-center p-12 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <img
              src={REELISLE_LOGO_WIDE}
              alt="Juwa.US"
              className="h-14 w-auto"
              data-testid="auth-brand-logo"
              style={{ filter: 'drop-shadow(0 4px 12px rgba(212,175,55,0.35))' }}
            />
          </div>

          {/* Attention-grabbing bonus chip — anchors the hero with the
              specific value-prop the user wants front and centre. */}
          <div
            className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.16em] mb-4"
            style={{
              background: 'rgba(212,175,55,0.16)',
              border: '1px solid rgba(212,175,55,0.5)',
              color: '#F5D87A',
              boxShadow: '0 6px 20px rgba(212,175,55,0.18)',
            }}
            data-testid="hero-bonus-chip"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Island access
          </div>

          <h1 className="text-5xl font-bold mb-4 leading-tight">
            Your Tropical<br />
            <span className="gold-text">Island Is Ready</span>
          </h1>

          <div
            className="rounded-2xl p-5 mb-6 relative overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(245,216,122,0.10) 50%, rgba(108,92,231,0.10) 100%)',
              border: '1px solid rgba(212,175,55,0.4)',
              boxShadow: '0 14px 40px rgba(212,175,55,0.18)',
            }}
            data-testid="hero-bonus-card"
          >
            <div
              className="absolute -top-4 -right-4 w-24 h-24 rounded-full opacity-30 blur-2xl"
              style={{ background: 'radial-gradient(circle, #F5D87A, transparent)' }}
            />
            <div className="flex items-start gap-3 relative">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37, #F5D87A)',
                  boxShadow: '0 8px 24px rgba(212,175,55,0.35)',
                }}
              >
                <Gift className="w-6 h-6 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#F5D87A]/80 font-semibold">
                  Secure access
                </p>
                <p className="text-2xl font-black text-white leading-tight mt-0.5">
                  Create your <span className="text-[#F5D87A]">account</span>
                </p>
                <p className="text-[12.5px] text-gray-300 mt-1.5 leading-relaxed">
                  Join Juwa.US for account access, quick support, and a smooth mobile experience.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-1">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs text-gray-200 font-semibold">Secure Access</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
              <span className="text-xs text-gray-200 font-semibold">Quick Setup</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Sparkles className="w-4 h-4 text-[#F5D87A]" />
              <span className="text-xs text-gray-200 font-semibold">24/7 Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="w-full max-w-full overflow-x-hidden flex items-center justify-center" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(212,175,55,0.11), transparent 35%), #0B0E14' }}>
        <div className="w-full max-w-sm min-w-0 mx-auto">
          {/* Mobile Logo */}
          <div className="flex items-center justify-center mb-6">
            <img
              src={REELISLE_LOGO_WIDE}
              alt="Juwa.US"
              className="h-24 w-auto object-contain"
              data-testid="auth-brand-logo-mobile"
            />
          </div>

          <div className="rounded-[28px] p-5 sm:p-7 border border-white/10 shadow-[0_24px_70px_rgba(0,0,0,0.42)]" style={{ background: 'rgba(19,24,34,0.88)' }}>
            {!staffOnlyPortal && displayBanners.length > 0 && (
              <div className="mb-5 rounded-xl overflow-hidden relative" data-testid="login-banners">
                <div
                  className={`flex ${bannerTransition ? 'transition-transform duration-500 ease-out' : ''}`}
                  style={{ transform: `translateX(-${bannerIdx * 100}%)` }}
                  onTransitionEnd={handleBannerTransitionEnd}
                >
                  {authCarouselBanners.map((banner, index) => (
                    <img
                      key={`${banner.id || 'auth-banner'}-${index}`}
                      src={bannerUrl(banner)}
                      alt={banner?.title || 'banner'}
                      className="h-36 w-full shrink-0 object-cover"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  ))}
                </div>
                {displayBanners.length > 1 && (
                  <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between px-1">
                    <button onClick={() => { setBannerTransition(true); setBannerIdx(i => (i - 1 + displayBanners.length) % displayBanners.length); }} className="w-6 h-6 rounded-full bg-black/40 flex items-center justify-center"><ChevronLeft className="w-3 h-3" /></button>
                    <button onClick={() => { setBannerTransition(true); setBannerIdx(i => i + 1); }} className="w-6 h-6 rounded-full bg-black/40 flex items-center justify-center"><ChevronRight className="w-3 h-3" /></button>
                  </div>
                )}
                {displayBanners.length > 1 && (
                  <div className="flex justify-center gap-1 py-1 bg-black/40">
                    {displayBanners.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === visibleBannerIdx ? 'bg-white' : 'bg-white/30'}`} />)}
                  </div>
                )}
              </div>
            )}

            <h2 className="text-3xl font-black text-center mb-2 tracking-tight">
              {adminPortal ? 'Admin Portal' : agentPortal ? (mode === 'register' ? 'Partner Sign Up' : 'Partner Sign In') : mode === 'forgot' ? 'Reset Password' : mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-gray-400 text-center mb-5 text-sm">
              {adminPortal
                ? 'Authorized Juwa.US staff access only'
                : agentPortal
                ? mode === 'register'
                  ? 'Create your partner account and manage your invite link.'
                  : 'Sign in to your Juwa.US partner portal.'
                : mode === 'forgot'
                ? resetSent
                  ? 'Enter the code from your email and choose a new password'
                  : 'We will email a reset code if the account exists'
                : mode === 'login'
                  ? 'Sign in to continue'
                  : 'Create your account for secure access and 24/7 support'}
            </p>

            {/* Public account access strip for login and signup modes. */}
            {!adminPortal && mode !== 'forgot' && !isSignupOtpStep && (
              <button
                type="button"
                onClick={() => switchAuthMode('register')}
                className="w-full mb-6 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition hover:scale-[1.015] active:scale-[0.99] text-left relative overflow-hidden group"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(212,175,55,0.22) 0%, rgba(245,216,122,0.12) 50%, rgba(108,92,231,0.12) 100%)',
                  border: '1px solid rgba(212,175,55,0.5)',
                  boxShadow: '0 10px 28px rgba(212,175,55,0.18)',
                }}
                data-testid="auth-bonus-promo"
              >
                <span
                  className="absolute -inset-1 opacity-25 blur-2xl group-hover:opacity-40 transition"
                  style={{
                    background:
                      'radial-gradient(circle at 18% 50%, #F5D87A, transparent 55%)',
                    pointerEvents: 'none',
                  }}
                />
                <span
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 relative"
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37, #F5D87A)',
                    boxShadow: '0 6px 18px rgba(212,175,55,0.4)',
                  }}
                >
                  <Gift className="w-5 h-5 text-black" />
                </span>
                <span className="flex-1 min-w-0 relative">
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: '#F5D87A' }}>
                    <Sparkles className="w-3 h-3" />
                    {agentPortal ? 'Partner access' : mode === 'login' ? 'New here?' : 'Island access'}
                  </span>
                  <span className="block text-[15px] font-black text-white leading-tight mt-0.5">
                    {agentPortal
                      ? 'Manage invite activity'
                      : mode === 'register'
                        ? 'Create your account'
                        : 'Start in seconds'}
                  </span>
                  <span className="block text-[11.5px] text-gray-300 mt-0.5">
                    {agentPortal
                      ? 'Tiered partner dashboard'
                      : 'Secure access with 24/7 support'}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-[#F5D87A] shrink-0 relative" />
              </button>
            )}

            {/* Mode Toggle */}
            {staffOnlyPortal ? null : mode === 'forgot' ? (
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setResetSent(false);
                  setResetCode('');
                  setResetPassword('');
                  setEmailError('');
                }}
                className="w-full mb-6 py-2 rounded-lg bg-[#0B0E14] text-sm font-medium text-[#D4AF37] hover:bg-white/5 transition"
                data-testid="back-to-login"
              >
                Back to Sign In
              </button>
            ) : isSignupOtpStep ? null : (
              <div
                className="grid grid-cols-2 rounded-2xl bg-[#060912]/90 p-1.5 mb-6 border border-[#F5D76E]/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_14px_32px_rgba(0,0,0,0.28)]"
                data-testid="auth-mode-toggle"
              >
                <button
                  type="button"
                  onClick={() => switchAuthMode('login')}
                  className={`h-11 rounded-xl text-sm font-black transition-all ${
                    mode === 'login'
                      ? 'bg-gradient-to-r from-[#14E07A] to-[#7CFFB2] text-[#03140b] shadow-[0_0_24px_rgba(20,224,122,0.28)]'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid="login-tab"
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => switchAuthMode('register')}
                  className={`h-11 rounded-xl text-sm font-black transition-all ${
                    mode === 'register'
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] text-black shadow-[0_0_24px_rgba(245,215,110,0.30)]'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid="register-tab"
                >
                  Sign Up
                </button>
              </div>
            )}

            {isSignupOtpStep ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-otp-step">
              <div className="rounded-[28px] border border-[#F5D76E]/40 bg-[#070A10]/70 p-5 text-center shadow-[0_18px_52px_rgba(0,0,0,0.30)]">
                <div
                  className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(20,224,122,0.95), rgba(245,215,110,0.95))',
                    boxShadow: '0 0 34px rgba(20,224,122,0.25), 0 0 34px rgba(245,215,110,0.20)',
                  }}
                >
                  <ShieldCheck className="h-8 w-8 text-[#03140b]" />
                </div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#F5D76E]">Email Verification</p>
                <h3 className="mt-1 text-2xl font-black text-white">Enter your OTP</h3>
                <p className="mx-auto mt-2 max-w-[260px] text-sm leading-relaxed text-gray-400">
                  We sent a 6-digit code to <span className="font-bold text-white">{email.trim()}</span>.
                </p>
                <p className="mx-auto mt-2 max-w-[290px] text-xs font-semibold leading-relaxed text-[#F5D76E]/85">
                  If it is not in your inbox, check Promotions or Spam, then tap Resend.
                </p>
                <div className="mt-5 flex items-center gap-2 rounded-2xl border border-white/10 bg-[#0B0E14] p-2">
                  <Input
                    data-testid="signup-email-otp-input"
                    type="text"
                    inputMode="numeric"
                    value={signupOtp}
                    onChange={(e) => setSignupOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="h-14 flex-1 border-0 bg-transparent text-center text-3xl font-black tracking-[0.22em] text-white placeholder:text-white/18 focus-visible:ring-0"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                  <button
                    type="button"
                    onClick={pasteSignupOtp}
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/8 text-[#F5D76E] transition hover:bg-white/12 active:scale-95"
                    data-testid="signup-paste-otp-btn"
                    aria-label="Paste OTP code"
                  >
                    <ClipboardPaste className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSignupStep('form');
                      setSignupOtp('');
                    }}
                    className="h-11 rounded-full border border-white/10 bg-white/5 text-sm font-black text-gray-200 transition hover:bg-white/10"
                    data-testid="signup-edit-details-btn"
                  >
                    Edit Details
                  </button>
                  <button
                    type="button"
                    onClick={() => sendSignupOtp(email)}
                    disabled={sendingSignupOtp}
                    className="h-11 rounded-full border border-[#F5D76E]/35 bg-[#F5D76E]/10 text-sm font-black text-[#F5D76E] transition hover:bg-[#F5D76E]/16 disabled:opacity-60"
                    data-testid="signup-resend-otp-btn"
                  >
                    {sendingSignupOtp ? 'Sending...' : 'Resend'}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || sendingSignupOtp}
                className="h-13 min-h-[52px] w-full rounded-full bg-gradient-to-r from-[#14E07A] via-[#7CFFB2] to-[#F5D76E] text-base font-black text-[#03140b] shadow-[0_0_34px_rgba(20,224,122,0.26),0_16px_34px_rgba(245,215,110,0.16)] transition hover:brightness-110 disabled:opacity-65"
                data-testid="submit-btn"
              >
                {loading || sendingSignupOtp ? (
                  <div className="spinner w-5 h-5 border-2 border-black/30 border-t-black" />
                ) : (
                  <>
                    {agentPortal ? 'Verify & Sign Up' : 'Verify & Create Account'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  {mode === 'register' ? 'Email' : mode === 'forgot' ? 'Username or Email' : 'Username or Email'}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid={mode === 'register' ? 'email-input' : 'identifier-input'}
                    type={mode === 'register' ? 'email' : 'text'}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (mode === 'register') resetSignupEmailVerification();
                      if (emailError) setEmailError('');
                    }}
                    placeholder={mode === 'register' ? 'Enter your email' : 'Enter username or email'}
                    className={`pl-10 bg-[#0B0E14] text-white h-11 ${emailError ? 'border-red-500 focus-visible:ring-red-500' : 'border-white/10'}`}
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'auth-identifier-error' : undefined}
                  />
                </div>
                {emailError && (
                  <p
                    id="auth-identifier-error"
                    className="mt-1.5 text-xs text-red-400 flex items-center gap-1"
                    data-testid="auth-identifier-error"
                  >
                    {emailError}
                    {mode === 'register' && /in use|registered|taken|exists/i.test(emailError) && (
                      <button
                        type="button"
                        onClick={() => { setMode('login'); setEmailError(''); }}
                        className="underline text-red-300 hover:text-red-200 ml-1"
                        data-testid="switch-to-login-link"
                      >
                        Sign in instead →
                      </button>
                    )}
                  </p>
                )}
              </div>

              {mode !== 'forgot' && (
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      data-testid="password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (mode === 'register') resetSignupEmailVerification();
                      }}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 bg-[#0B0E14] border-white/10 text-white h-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              )}

              {mode === 'forgot' && resetSent && (
                <>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Reset Code</label>
                    <Input
                      data-testid="reset-code-input"
                      type="text"
                      inputMode="numeric"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit code"
                      className="bg-[#0B0E14] border-white/10 text-white h-11"
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        data-testid="reset-password-input"
                        type={showResetPassword ? 'text' : 'password'}
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        placeholder="Create a new password"
                        className="pl-10 pr-10 bg-[#0B0E14] border-white/10 text-white h-11"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                      >
                        {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="mt-1.5 text-[11px] text-gray-500">
                      Use at least 8 characters with uppercase, lowercase, and a number.
                    </p>
                  </div>
                </>
              )}

              {!staffOnlyPortal && !agentPortal && mode === 'register' && (
                <div
                  className="rounded-2xl border border-[#F5D76E]/50 bg-[#F5D76E]/10 overflow-hidden"
                  data-testid="referral-signup-card"
                >
                  <button
                    type="button"
                    onClick={() => setReferralOpen((open) => !open)}
                    className="w-full flex items-center gap-2 px-3 py-3 text-left"
                    data-testid="referral-toggle-btn"
                  >
                    <Tag className="w-4 h-4 text-[#F5D76E] shrink-0" />
                    <span className="text-sm font-semibold text-white flex-1">Have a referral code?</span>
                    {referralOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  {referralOpen && (
                    <div className="px-3 pb-3 space-y-2">
                      <label className="block text-[11px] text-gray-400">Enter referral code (optional)</label>
                      <div className="relative">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5D76E]" />
                        <Input
                          data-testid="referral-input"
                          type="text"
                          value={referralCode}
                          onChange={(e) => {
                            setReferralCode(e.target.value);
                            setReferralStatus({ state: e.target.value.trim() ? 'idle' : 'empty', message: '' });
                            resetSignupEmailVerification();
                          }}
                          placeholder="#R1LSAF"
                          className={`pl-10 bg-[#0B0E14] text-white h-11 uppercase ${
                            referralStatus.state === 'invalid'
                              ? 'border-red-400 focus-visible:ring-red-400'
                              : referralStatus.state === 'valid'
                                ? 'border-emerald-400 focus-visible:ring-emerald-400'
                                : 'border-[#F5D76E]/60'
                          }`}
                        />
                      </div>
                      {referralStatus.message && (
                        <p
                          className={`text-[11px] flex items-center gap-1 ${
                            referralStatus.state === 'valid' ? 'text-emerald-300' :
                            referralStatus.state === 'invalid' ? 'text-red-300' : 'text-gray-400'
                          }`}
                          data-testid="referral-status"
                        >
                          {referralStatus.state === 'valid' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                          {referralStatus.message}
                        </p>
                      )}
                      {!referralStatus.message && (
                        <p className="text-[11px] text-gray-500">Apply a valid invite code if you have one.</p>
                      )}
                      <Button
                        type="button"
                        onClick={() => validateReferralCode()}
                        disabled={validatingReferral || !referralCode.trim()}
                        className="w-full h-10 rounded-xl bg-[#F5D76E] text-black font-black hover:bg-[#FFE88A] disabled:opacity-60"
                        data-testid="apply-referral-btn"
                      >
                        {validatingReferral ? 'Checking...' : referralStatus.state === 'valid' ? 'Code Applied' : 'Apply Code'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || sendingSignupOtp}
                className={`w-full h-12 rounded-full font-black transition-all ${
                  mode === 'register'
                    ? 'bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#FFE88A] text-black shadow-[0_0_30px_rgba(245,215,110,0.30),0_14px_28px_rgba(212,175,55,0.15)] hover:brightness-110'
                    : 'bg-[#D4AF37] text-black hover:bg-[#E5C158]'
                }`}
                data-testid="submit-btn"
              >
                {loading || sendingSignupOtp ? (
                  <div className="spinner w-5 h-5 border-2 border-black/30 border-t-black" />
                ) : (
                  <>
                    {mode === 'forgot'
                      ? resetSent ? 'Reset Password' : 'Send Reset Code'
                      : mode === 'login' ? 'Sign In' : 'Sign Up'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
            )}

            {!staffOnlyPortal && mode === 'login' && (
              <p className="text-center text-sm text-gray-500 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setMode('forgot');
                    setResetSent(false);
                    setResetCode('');
                    setResetPassword('');
                    setPassword('');
                    setEmailError('');
                  }}
                  className="text-[#D4AF37] hover:underline"
                  data-testid="forgot-password-btn"
                >
                  Forgot password?
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
      {!staffOnlyPortal && supportLinks.length > 0 && (
        <div className="fixed left-4 sm:left-6 bottom-5 sm:bottom-7 z-[60] flex flex-col items-start gap-3" data-testid="auth-support-launcher">
          {supportOpen && (
            <div
              className="w-[min(88vw,310px)] rounded-3xl overflow-hidden"
              style={{
                background: 'rgba(6,9,18,0.96)',
                border: '1px solid rgba(245,215,110,0.28)',
                boxShadow: '0 22px 70px rgba(0,0,0,0.58)',
                backdropFilter: 'blur(16px)',
              }}
              data-testid="auth-support-panel"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div>
                  <p className="text-sm font-black text-white">Support</p>
                  <p className="text-[11px] text-gray-400">Message us anytime</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSupportOpen(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white/80 hover:text-white active:scale-95"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
                  aria-label="Close support options"
                  data-testid="auth-support-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-3 space-y-2">
                {supportLinks.map((channel) => (
                  <a
                    key={channel.key}
                    href={channel.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setSupportOpen(false)}
                    className="flex items-center gap-3 rounded-2xl px-3.5 py-3 text-sm font-black text-white transition active:scale-[0.98] hover:brightness-110"
                    style={{
                      background: `linear-gradient(135deg, ${channel.color}24, rgba(255,255,255,0.04))`,
                      border: `1px solid ${channel.color}55`,
                    }}
                    data-testid={`auth-support-${channel.key}`}
                  >
                    <span
                      className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
                      style={{ background: `${channel.color}22`, border: `1px solid ${channel.color}55` }}
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24" className="w-5 h-5" fill={channel.color}>
                        <path d={channel.path} />
                      </svg>
                    </span>
                    <span>{channel.label}</span>
                    <ArrowRight className="ml-auto w-4 h-4 text-white/60" />
                  </a>
                ))}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setSupportOpen((open) => !open)}
            className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-[#03140b] active:scale-95 transition"
            style={{
              background: 'linear-gradient(135deg, #14E07A, #F5D76E)',
              border: '1px solid rgba(255,255,255,0.45)',
              boxShadow: '0 0 0 8px rgba(20,224,122,0.10), 0 18px 46px rgba(20,224,122,0.42), 0 0 44px rgba(245,215,110,0.34)',
            }}
            aria-expanded={supportOpen}
            aria-label={supportOpen ? 'Close support options' : 'Open support options'}
            data-testid="auth-support-button"
          >
            <svg viewBox="0 0 24 24" className="w-8 h-8 sm:w-9 sm:h-9" fill="currentColor" aria-hidden="true">
              <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.81c.02.63.66 1.03 1.24.78l2.02-.89c.17-.08.36-.1.55-.06.88.24 1.82.37 2.72.37 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm5.89 7.41l-2.89 4.58c-.46.73-1.44.92-2.13.42l-2.3-1.73c-.17-.13-.41-.13-.58 0L7.5 14.67c-.31.24-.72-.13-.52-.47l2.89-4.58c.46-.73 1.44-.92 2.13-.42l2.3 1.73c.17.13.41.13.58 0l2.49-1.99c.31-.24.72.13.52.47z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
